/*
 * CookieBanner — GDPR/RODO compliant cookie consent
 * Categories: necessary (always on), analytics (Google Analytics), marketing (Google Ads)
 * Stores consent in localStorage under "cookie_consent"
 */
import { useState, useEffect } from "react";
import { Link } from "wouter";

type ConsentState = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
};

const STORAGE_KEY = "cookie_consent_v1";

function loadConsent(): ConsentState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveConsent(consent: ConsentState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  // Push to dataLayer for Google Tag Manager / Google Ads
  if (typeof window !== "undefined" && (window as any).gtag) {
    (window as any).gtag("consent", "update", {
      analytics_storage: consent.analytics ? "granted" : "denied",
      ad_storage: consent.marketing ? "granted" : "denied",
      ad_user_data: consent.marketing ? "granted" : "denied",
      ad_personalization: consent.marketing ? "granted" : "denied",
    });
  }
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const saved = loadConsent();
    if (!saved) {
      // Show banner after short delay
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
    // Apply saved consent
    saveConsent(saved);
  }, []);

  if (!visible) return null;

  const acceptAll = () => {
    const consent: ConsentState = { necessary: true, analytics: true, marketing: true };
    saveConsent(consent);
    setVisible(false);
  };

  const acceptSelected = () => {
    const consent: ConsentState = { necessary: true, analytics, marketing };
    saveConsent(consent);
    setVisible(false);
  };

  const rejectAll = () => {
    const consent: ConsentState = { necessary: true, analytics: false, marketing: false };
    saveConsent(consent);
    setVisible(false);
  };

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6"
      style={{ pointerEvents: "none" }}
    >
      <div
        className="max-w-3xl mx-auto rounded-2xl shadow-2xl overflow-hidden"
        style={{
          background: "oklch(0.18 0.05 155)",
          border: "1px solid oklch(0.35 0.08 155)",
          pointerEvents: "all",
        }}
      >
        {/* Main row */}
        <div className="p-5 md:p-6">
          <div className="flex items-start gap-3 mb-4">
            <span className="text-2xl flex-shrink-0">🍪</span>
            <div>
              <h3
                className="text-white font-bold text-base mb-1"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Używamy plików cookies
              </h3>
              <p
                className="text-white/60 text-xs leading-relaxed"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Stosujemy pliki cookies niezbędne do działania serwisu oraz — za Twoją zgodą — analityczne (Google Analytics) i marketingowe (Google Ads). Możesz zaakceptować wszystkie, wybrać kategorie lub odrzucić opcjonalne.{" "}
                <Link href="/polityka-prywatnosci" className="underline text-white/80 hover:text-white">
                  Polityka prywatności
                </Link>
              </p>
            </div>
          </div>

          {/* Details toggle */}
          {showDetails && (
            <div className="mb-4 space-y-3 border-t border-white/10 pt-4">
              {/* Necessary */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white text-sm font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Niezbędne
                  </p>
                  <p className="text-white/50 text-xs" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Wymagane do działania serwisu. Zawsze aktywne.
                  </p>
                </div>
                <div
                  className="w-10 h-5 rounded-full flex items-center px-0.5"
                  style={{ background: "oklch(0.55 0.15 155)" }}
                >
                  <div className="w-4 h-4 rounded-full bg-white ml-auto" />
                </div>
              </div>
              {/* Analytics */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white text-sm font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Analityczne (Google Analytics)
                  </p>
                  <p className="text-white/50 text-xs" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Anonimowe dane o ruchu — pomagają nam ulepszać serwis.
                  </p>
                </div>
                <button
                  onClick={() => setAnalytics(!analytics)}
                  className="w-10 h-5 rounded-full flex items-center px-0.5 transition-colors"
                  style={{ background: analytics ? "oklch(0.62 0.18 45)" : "oklch(0.35 0.05 155)" }}
                  aria-label="Przełącz cookies analityczne"
                >
                  <div
                    className="w-4 h-4 rounded-full bg-white transition-transform"
                    style={{ transform: analytics ? "translateX(20px)" : "translateX(0)" }}
                  />
                </button>
              </div>
              {/* Marketing */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white text-sm font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Marketingowe (Google Ads)
                  </p>
                  <p className="text-white/50 text-xs" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Umożliwiają wyświetlanie spersonalizowanych reklam.
                  </p>
                </div>
                <button
                  onClick={() => setMarketing(!marketing)}
                  className="w-10 h-5 rounded-full flex items-center px-0.5 transition-colors"
                  style={{ background: marketing ? "oklch(0.62 0.18 45)" : "oklch(0.35 0.05 155)" }}
                  aria-label="Przełącz cookies marketingowe"
                >
                  <div
                    className="w-4 h-4 rounded-full bg-white transition-transform"
                    style={{ transform: marketing ? "translateX(20px)" : "translateX(0)" }}
                  />
                </button>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={acceptAll}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white transition-opacity hover:opacity-90"
              style={{ background: "oklch(0.50 0.18 45)", fontFamily: "'Outfit', sans-serif" }}
            >
              Akceptuj wszystkie
            </button>
            {showDetails ? (
              <button
                onClick={acceptSelected}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white transition-opacity hover:opacity-90"
                style={{ background: "oklch(0.45 0.12 155)", fontFamily: "'Outfit', sans-serif" }}
              >
                Zapisz wybór
              </button>
            ) : (
              <button
                onClick={() => setShowDetails(true)}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white/70 hover:text-white transition-colors"
                style={{ background: "oklch(0.28 0.06 155)", fontFamily: "'Outfit', sans-serif" }}
              >
                Dostosuj
              </button>
            )}
            <button
              onClick={rejectAll}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white/50 hover:text-white/80 transition-colors"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Odrzuć opcjonalne
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
